import * as React from 'react';

import { cn } from '@mint-lib/tw-utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Separator as SeparatorPrimitive } from 'radix-ui';

import { typographyVariants } from './typography.jsx';

const dividerVariants = cva(
  'shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px',
  {
    variants: {
      color: {
        light: 'bg-border-secondary',
        dark: 'bg-border-primary',
      },
    },
  },
);

function Divider({
  className,
  orientation = 'horizontal',
  decorative = true,
  children,
  color = 'dark',
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root> &
  VariantProps<typeof dividerVariants>) {
  return (
    <SeparatorPrimitive.Root
      data-slot="separator"
      decorative={decorative}
      orientation={orientation}
      className={cn(dividerVariants({ color }), className)}
      {...props}
    />
  );
}

function DividerWithText({
  className,
  orientation = 'horizontal',
  decorative = true,
  children,
  color = 'dark',
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root> &
  VariantProps<typeof dividerVariants>) {
  return (
    <div
      className={cn(
        'grid items-center gap-2',
        orientation === 'horizontal'
          ? 'grid-cols-[1fr_auto_1fr]'
          : 'grid-rows-[1fr_min-content_1fr] justify-items-center',
      )}
    >
      <SeparatorPrimitive.Root
        data-slot="separator"
        decorative={decorative}
        orientation={orientation}
        className={cn(dividerVariants({ color }), className)}
        {...props}
      />
      <div
        className={cn(
          'h-fit w-fit',
          typographyVariants({ variant: 'bodyShort01', color: 'secondary' }),
        )}
      >
        {children}
      </div>
      <SeparatorPrimitive.Root
        data-slot="separator"
        decorative={decorative}
        orientation={orientation}
        className={cn(dividerVariants({ color }), className)}
        {...props}
      />
    </div>
  );
}

export { Divider, DividerWithText };
